import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { resolveClientDestination } from '@/utils/clientRouting';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Paper,
  Stack,
  Alert,
  Chip,
  useTheme,
  Grid,
  CircularProgress,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Draw as DrawIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  FolderOpen as BinderIcon,
  Description as TaxIcon,
} from '@mui/icons-material';
import {
  AuditBinderStage,
  BinderStage,
  ServiceLine,
  type AuditDashboardItemDto,
  type TaxBinderDto,
} from '@ata-digital/client-tax-system-api-client';
import { hasOutstandingSignatureHints } from '@/utils/eSignatureHelpers';
import { getClientStageLabel, getClientStageColor } from '@/features/tax-binders/utils/taxBinderDisplayUtils';
import { TaxBinderTypeChip } from '@/components/common/TaxBinderTypeChip';
import { taxBinderQueryOptions } from '@/lib/query/options';
import { useAuthState } from '@/hooks/auth/useAuth';
import { useClient } from '@/hooks/context/useClient';
import { useAuditDashboard } from '@/hooks/api/useAuditDashboard';
import { AuditBinderCard } from '@/components/binders/AuditBinderCard';
import { SERVICE_LINE_CONFIG } from '@/components/common/ServiceLineBadges';

const CLIENT_DASHBOARD_AUDIT_STAGES = [AuditBinderStage.Open, AuditBinderStage.InReview, AuditBinderStage.Complete];

export const Route = createFileRoute('/')({
  // Active-client resolution (auto-select single client / redirect multi-client
  // to /client-select) is handled centrally in the root route's beforeLoad, so
  // by the time this loader runs a client is already selected.
  loader: async ({ context: { queryClient } }) => {
    const authState = queryClient.getQueryData(['auth', 'session']) as { token?: string } | undefined;
    const token = authState?.token;
    const selectedClientId = sessionStorage.getItem('selectedClientId') || undefined;

    if (token && selectedClientId) {
      const destination = await resolveClientDestination(queryClient, token, selectedClientId);

      // If the destination isn't `/`, redirect before the component renders
      if (destination.to !== '/') {
        throw redirect(destination as Parameters<typeof redirect>[0]);
      }
    }
  },
  component: HomePage,
});

type ActiveBinder =
  | { kind: 'tax'; createdAt: number; binder: TaxBinderDto }
  | { kind: 'audit'; createdAt: number; binder: AuditDashboardItemDto };

function HomePage() {
  const theme = useTheme();
  const { data: authState } = useAuthState();
  const { selectedClient } = useClient();

  // Only surface binder types for service lines enabled on the active client
  const hasTaxServiceLine = selectedClient?.serviceLines?.includes(ServiceLine.Tax) ?? false;
  const hasAuditServiceLine = selectedClient?.serviceLines?.includes(ServiceLine.Audit) ?? false;

  // Gate each binder query on the active client's service lines so we don't fetch (or keep the
  // loading state pending on) data that won't be displayed. Disabled queries stay idle in
  // TanStack Query v5, so isLoading won't be held true by a query we never intend to run.
  const { data: bindersResponse, isLoading: isTaxLoading } = useQuery({
    ...taxBinderQueryOptions.list({}, authState?.token || undefined, selectedClient?.id || undefined),
    enabled: !!authState?.isAuthenticated && !!authState?.token && hasTaxServiceLine,
  });
  // Completed audit binders stay on the dashboard while their client access window is open; the API drops them after.
  const { data: auditResponse, isLoading: isAuditLoading } = useAuditDashboard(
    { stages: CLIENT_DASHBOARD_AUDIT_STAGES },
    { activeClientId: selectedClient?.id, enabled: hasAuditServiceLine }
  );

  const isLoading = isTaxLoading || isAuditLoading;

  // Extract binders from API responses, keeping only active (non-complete) tax binders
  const activeTaxBinders = hasTaxServiceLine
    ? (bindersResponse?.data?.items || []).filter((binder) => binder.stage !== BinderStage.Complete)
    : [];
  const activeAuditBinders = hasAuditServiceLine ? auditResponse?.data?.items || [] : [];

  // Merge tax + audit binders into a single list, most recently created first
  const activeBinders: ActiveBinder[] = [
    ...activeTaxBinders.map(
      (binder): ActiveBinder => ({
        kind: 'tax',
        createdAt: binder.createdAt ? new Date(binder.createdAt).getTime() : 0,
        binder,
      })
    ),
    ...activeAuditBinders.map(
      (binder): ActiveBinder => ({
        kind: 'audit',
        createdAt: binder.createdAt ? new Date(binder.createdAt).getTime() : 0,
        binder,
      })
    ),
  ].sort((a, b) => b.createdAt - a.createdAt);

  // Show loading state if data hasn't been prefetched yet
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {/* Welcome Section */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Welcome to Client Portal
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Manage your tax documents, complete questionnaires, and track your progress all in one place.
        </Typography>
      </Paper>

      {/* Current Binders */}
      <Box>
        <Typography variant="h4" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BinderIcon />
          Current Binders
        </Typography>

        {activeBinders.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <BinderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Active Binders
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You don't have any active binders at the moment. Contact your tax preparer to get started.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {activeBinders.map((entry) =>
              entry.kind === 'tax' ? (
                <Grid key={`tax-${entry.binder.id}`} size={{ xs: 12, md: 6 }}>
                  <TaxBinderCard binder={entry.binder} theme={theme} />
                </Grid>
              ) : (
                <Grid key={`audit-${entry.binder.id}`} size={{ xs: 12, md: 6 }}>
                  <AuditBinderCard binder={entry.binder} />
                </Grid>
              )
            )}
          </Grid>
        )}
      </Box>

      {/* Urgent Items */}
      {activeTaxBinders.some(
        (binder) => binder.dueDate && new Date(binder.dueDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      ) && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon />
            <Typography variant="body2">
              You have binders with upcoming due dates. Please review and complete them soon.
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>
  );
}

function TaxBinderCard({ binder, theme }: { binder: TaxBinderDto; theme: Theme }) {
  return (
    <Card sx={{ height: '100%', '&:hover': { boxShadow: theme.shadows[4] } }}>
      <CardActionArea component={Link} to={`/tax-binders/${binder.id}`}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Chip
              icon={<TaxIcon />}
              label={SERVICE_LINE_CONFIG[ServiceLine.Tax].label}
              size="small"
              color={SERVICE_LINE_CONFIG[ServiceLine.Tax].color}
              variant="outlined"
            />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <TaxBinderTypeChip type={binder.type} />
              <Chip label={binder.taxYear || 'N/A'} color="primary" size="small" />
            </Box>
          </Box>

          <Typography variant="h6" component="div">
            {binder.name || 'Untitled Binder'}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {binder.preparerName ? 'Assigned Preparer: ATA' : 'Managed by: ATA'}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {binder.completionStatus && (
              <Chip
                label={`${binder.completionStatus.uploadedDocuments || 0}/${binder.completionStatus.totalDocuments || 0} documents`}
                size="small"
                variant="outlined"
              />
            )}
            {/* This card is the client's first screen, so it has to answer "why is my filed return
                being worked on?" the same way the binder list and detail already do */}
            <Chip
              label={
                binder.isAmendmentInProgress
                  ? getClientStageLabel(binder.stage, true)
                  : binder.stage === BinderStage.Complete
                    ? 'Complete'
                    : binder.stage === BinderStage.EfileReady
                      ? 'E-File Ready'
                      : 'In Progress'
              }
              size="small"
              color={
                binder.isAmendmentInProgress
                  ? getClientStageColor(binder.stage, true)
                  : binder.stage === BinderStage.Complete
                    ? 'success'
                    : binder.stage === BinderStage.EfileReady
                      ? 'info'
                      : 'warning'
              }
            />
            {/* Outstanding Signature Warning */}
            {hasOutstandingSignatureHints(binder) && (
              <Chip icon={<DrawIcon />} label="Signature Required" size="small" color="error" variant="outlined" />
            )}
          </Stack>
        </CardContent>
        <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="primary">
            Open Binder
          </Typography>
          <ArrowForwardIcon fontSize="small" color="primary" />
        </Box>
      </CardActionArea>
    </Card>
  );
}
